/**
 * TripDayMediaSection - Bloc "Médias de cette journée" (Carnets V2)
 *
 * - Affiche uniquement les médias affectés (attach entity_type=trip_day)
 * - Ajouter depuis la médiathèque (attach)
 * - Retirer de la journée (detach) sans supprimer le Media
 * - Rotation directe sur chaque vignette
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { getMediaList, attachMedia, detachMedia, rotateMedia, uploadMediaBatchFromFiles, syncTripDayMedia } from "../lib/api";
import { Button } from "./ui/button";
import { Plus, Trash2, RotateCcw, RotateCw, Loader2, Image as ImageIcon, Upload, RefreshCw } from "lucide-react";
import MediaSelector from "./MediaSelector";

export default function TripDayMediaSection({ tripId, dayId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [rotating, setRotating] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getMediaList({ entity_type: "trip_day", entity_id: dayId, limit: 200 });
      // Trier par ordre d'assignment
      const sorted = [...(res.data || [])].sort((a, b) => {
        const ao = (a.attachments || []).find(x => x.entity_id === dayId)?.order || 0;
        const bo = (b.attachments || []).find(x => x.entity_id === dayId)?.order || 0;
        return ao - bo;
      });
      setItems(sorted);
    } catch (e) {
      toast.error("Erreur chargement médias");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!dayId) return;
    // Cohérence idempotente auto : rattache les médias référencés par blocs existants
    syncTripDayMedia(tripId, dayId).catch(() => {}).finally(() => load());
  }, [dayId]); // eslint-disable-line

  const handleUploadFromComputer = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const res = await uploadMediaBatchFromFiles(files);
      const created = res.data?.created || res.data?.items || res.data || [];
      // Rattacher chacun au TripDay
      const startOrder = items.length;
      for (let i = 0; i < created.length; i++) {
        const m = created[i];
        if (!m?.id) continue;
        try {
          await attachMedia(m.id, { entity_type: "trip_day", entity_id: dayId, order: startOrder + i });
        } catch { /* ignore attach failures individuellement */ }
      }
      toast.success(`${created.length} photo(s) uploadée(s) et rattachée(s) à la journée.`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur upload");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleResync = async () => {
    try {
      const res = await syncTripDayMedia(tripId, dayId);
      toast.success(`Cohérence rétablie · ${res.data.attached_new} nouveaux rattachements`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur sync");
    }
  };

  const handleAttach = async (selected) => {
    const list = Array.isArray(selected) ? selected : [selected];
    const startOrder = items.length;
    try {
      for (let i = 0; i < list.length; i++) {
        const m = list[i];
        // Skip si deja rattache
        if (items.find((x) => x.id === m.id)) continue;
        await attachMedia(m.id, { entity_type: "trip_day", entity_id: dayId, order: startOrder + i });
      }
      toast.success(`${list.length} média(s) rattaché(s)`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur rattachement");
    }
  };

  const handleDetach = async (mediaId) => {
    if (!window.confirm("Retirer ce média de la journée ? (Le Media n'est pas supprimé.)")) return;
    try {
      await detachMedia(mediaId, "trip_day", dayId);
      setItems((prev) => prev.filter((m) => m.id !== mediaId));
      toast.success("Média retiré de la journée.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur détachement");
    }
  };

  const handleRotate = async (mediaId, direction) => {
    setRotating(mediaId);
    try {
      const res = await rotateMedia(mediaId, { direction });
      setItems((prev) => prev.map((m) => m.id === mediaId ? { ...m, ...res.data, __ts: Date.now() } : m));
      toast.success(`Rotation ${direction === "cw" ? "↷" : "↶"} 90°`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur rotation");
    } finally {
      setRotating(null);
    }
  };

  return (
    <div data-testid="tripday-media-section">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <label className="block text-sm font-semibold text-primary">Photos de cette journée ({items.length})</label>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            onChange={handleUploadFromComputer}
            style={{ display: "none" }}
            data-testid="tripday-upload-input"
          />
          <Button type="button" variant="default" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="tripday-upload-btn">
            {uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
            Ajouter des photos (depuis l'ordinateur)
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => setPickerOpen(true)} data-testid="tripday-media-add">
            <Plus className="h-4 w-4 mr-1" /> Depuis la médiathèque
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={handleResync} title="Rattacher les médias déjà référencés dans le contenu" data-testid="tripday-sync-btn">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>
      {loading ? (
        <div className="text-sm text-muted-foreground py-4"><Loader2 className="h-4 w-4 inline animate-spin mr-1" /> Chargement...</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-muted-foreground text-center py-6 border border-dashed border-border rounded">
          Aucun média affecté. Cliquez sur « Ajouter depuis la médiathèque ».
        </div>
      ) : (
        <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))" }}>
          {items.map((m) => (
            <div key={m.id} className="relative group border border-border rounded overflow-hidden bg-secondary/20" style={{ aspectRatio: "1 / 1" }} data-testid={`tripday-media-${m.id}`}>
              {m.public_cloudinary_url || m.original_cloudinary_url ? (
                <img
                  src={(m.public_cloudinary_url || m.original_cloudinary_url) + (m.__ts ? `#${m.__ts}` : "")}
                  alt=""
                  loading="lazy"
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center"><ImageIcon className="h-8 w-8 text-primary/30" /></div>
              )}
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                <button type="button" onClick={() => handleRotate(m.id, "ccw")} disabled={rotating === m.id} className="bg-white/90 hover:bg-white text-primary rounded p-1" title="↶ 90°" data-testid={`tripday-media-${m.id}-rotate-ccw`}>
                  <RotateCcw className="h-3.5 w-3.5" />
                </button>
                <button type="button" onClick={() => handleRotate(m.id, "cw")} disabled={rotating === m.id} className="bg-white/90 hover:bg-white text-primary rounded p-1" title="↷ 90°" data-testid={`tripday-media-${m.id}-rotate-cw`}>
                  <RotateCw className="h-3.5 w-3.5" />
                </button>
                <button type="button" onClick={() => handleDetach(m.id)} className="bg-red-600/90 hover:bg-red-700 text-white rounded p-1" title="Retirer" data-testid={`tripday-media-${m.id}-detach`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
              {m.rotation ? (
                <span className="absolute bottom-1 right-1 bg-black/70 text-white text-[10px] px-1 rounded">↻ {m.rotation}°</span>
              ) : null}
            </div>
          ))}
        </div>
      )}
      <MediaSelector
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onSelect={handleAttach}
        multiple={true}
        type="photo"
      />
    </div>
  );
}
